import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import NotificationContainer from '../Notification/NotificationContainer';
import styles from './Profile.module.css';

const Profile = () => {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    region: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger le profil depuis l'API au montage
  useEffect(() => {
    loadProfile();
  }, []);

  // Mettre à jour formData quand user change
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || user.profile?.phone || '',
        address: user.profile?.address || '',
        city: user.profile?.city || '',
        region: user.profile?.region || ''
      });
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await authService.getProfile();
      // L'API retourne { success: true, data: { user: {...} } }
      const userData = response.data?.data?.user || response.data?.user || response.data;
      if (userData) {
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || userData.profile?.phone || '',
          address: userData.profile?.address || '',
          city: userData.profile?.city || '',
          region: userData.profile?.region || ''
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      // Ne pas afficher d'erreur si c'est juste une erreur d'authentification
      if (error.response?.status !== 401) {
        addNotification('Erreur lors du chargement du profil', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await updateProfile({
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        region: formData.region
      });

      if (result.success) {
        addNotification('Profil mis à jour avec succès', 'success');
        setIsEditing(false);
        await loadProfile(); // Recharger pour avoir les données à jour
      } else {
        addNotification(result.error || 'Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour du profil';
      addNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePassword = async () => {
    try {
      if (passwordData.new_password !== passwordData.new_password_confirmation) {
        addNotification('Les mots de passe ne correspondent pas', 'error');
        return;
      }

      if (passwordData.new_password.length < 8) {
        addNotification('Le mot de passe doit contenir au moins 8 caractères', 'error');
        return;
      }

      setSaving(true);
      const response = await authService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.new_password_confirmation
      });

      // L'API retourne { success: true, message: '...' }
      if (response.data?.success || response.success) {
        addNotification(response.data?.message || 'Mot de passe modifié avec succès', 'success');
        setIsEditingPassword(false);
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: ''
        });
      } else {
        addNotification(response.data?.message || 'Erreur lors du changement de mot de passe', 'error');
      }
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.current_password?.[0] || 'Erreur lors du changement de mot de passe';
      addNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <div className={styles.profile}>
        <div className={styles.loading}>
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <p>Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.profile}>
        <div className={styles.notAuthenticated}>
          <h2>Vous devez être connecté pour accéder à votre profil</h2>
          <button onClick={() => navigate('/login')} className={styles.loginButton}>
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profile}>
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />

      <div className={styles.profileContainer}>
        <div className={styles.profileHeader}>
          <div>
            <h1>Mon Profil</h1>
            <p className={styles.profileSubtitle}>Gérez vos informations personnelles et vos préférences</p>
          </div>
          {user.user_type === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className={styles.adminButton}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Tableau de bord Admin
            </button>
          )}
        </div>

        <div className={styles.profileContent}>
          <div className={styles.profileInfo}>
            <div className={styles.sectionHeader}>
              <h2>Informations personnelles</h2>
              {!isEditing && (
                <button onClick={() => setIsEditing(true)} className={styles.editButton}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modifier
                </button>
              )}
            </div>

            {isEditing ? (
              <form className={styles.editForm} onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name">Nom complet *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled
                      className={styles.disabledInput}
                    />
                    <small className={styles.helpText}>L'email ne peut pas être modifié</small>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="phone">Téléphone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+241 XX XX XX XX"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="city">Ville</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Libreville"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="region">Région</label>
                    <input
                      type="text"
                      id="region"
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      placeholder="Lagunes"
                    />
                  </div>

                  <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                    <label htmlFor="address">Adresse complète</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Rue, quartier, etc."
                    />
                  </div>
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      // Restaurer les données originales
                      if (user) {
                        setFormData({
                          name: user.name || '',
                          email: user.email || '',
                          phone: user.phone || user.profile?.phone || '',
                          address: user.profile?.address || '',
                          city: user.profile?.city || '',
                          region: user.profile?.region || ''
                        });
                      }
                    }}
                    className={styles.cancelButton}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={styles.saveButton}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Sauvegarder
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className={styles.profileDetails}>
                <div className={styles.detailRow}>
                  <strong>Nom complet:</strong>
                  <span>{formData.name || user.name || 'Non renseigné'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Email:</strong>
                  <span>{formData.email || user.email || 'Non renseigné'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Téléphone:</strong>
                  <span>{formData.phone || user.phone || user.profile?.phone || 'Non renseigné'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Ville:</strong>
                  <span>{formData.city || user.profile?.city || 'Non renseignée'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Région:</strong>
                  <span>{formData.region || user.profile?.region || 'Non renseignée'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Adresse:</strong>
                  <span>{formData.address || user.profile?.address || 'Non renseignée'}</span>
                </div>

                <div className={styles.detailRow}>
                  <strong>Type de compte:</strong>
                  <span className={styles.accountType}>
                    {user.user_type === 'admin' ? '👑 Administrateur' :
                     user.user_type === 'supplier' ? '🏪 Fournisseur' :
                     user.user_type === 'delivery_person' ? '🚚 Livreur' :
                     '👤 Client'}
                  </span>
                </div>
              </div>
            )}

            {/* Section changement de mot de passe */}
            <div className={styles.passwordSection}>
              <div className={styles.sectionHeader}>
                <h2>Sécurité</h2>
                {!isEditingPassword && (
                  <button onClick={() => setIsEditingPassword(true)} className={styles.editButton}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Changer le mot de passe
                  </button>
                )}
              </div>

              {isEditingPassword && (
                <form className={styles.editForm} onSubmit={(e) => { e.preventDefault(); handleSavePassword(); }}>
                  <div className={`${styles.formGrid} ${styles.passwordFormGrid}`}>
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                      <label htmlFor="current_password">Mot de passe actuel *</label>
                      <input
                        type="password"
                        id="current_password"
                        name="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        required
                      />
                    </div>

                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                      <label htmlFor="new_password">Nouveau mot de passe *</label>
                      <input
                        type="password"
                        id="new_password"
                        name="new_password"
                        value={passwordData.new_password}
                        onChange={handlePasswordChange}
                        required
                        minLength="8"
                      />
                      <small className={styles.helpText}>Minimum 8 caractères</small>
                    </div>

                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                      <label htmlFor="new_password_confirmation">Confirmer le nouveau mot de passe *</label>
                      <input
                        type="password"
                        id="new_password_confirmation"
                        name="new_password_confirmation"
                        value={passwordData.new_password_confirmation}
                        onChange={handlePasswordChange}
                        required
                        minLength="8"
                      />
                    </div>
                  </div>

                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingPassword(false);
                        setPasswordData({
                          current_password: '',
                          new_password: '',
                          new_password_confirmation: ''
                        });
                      }}
                      className={styles.cancelButton}
                      disabled={saving}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className={styles.saveButton}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className={styles.profileActions}>
            <div className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3>Mes commandes</h3>
              <p>Consultez l'historique de vos commandes</p>
              <button className={styles.actionButton} onClick={() => navigate('/orders')}>
                Voir mes commandes
              </button>
            </div>

            <div className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3>Mes favoris</h3>
              <p>Produits que vous avez ajoutés à vos favoris</p>
              <button className={styles.actionButton} onClick={() => navigate('/wishlist')}>
                Voir mes favoris
              </button>
            </div>

            <div className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3>Paramètres</h3>
              <p>Gérez vos préférences et notifications</p>
              <button className={styles.actionButton}>Paramètres</button>
            </div>

            <div className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h3>Déconnexion</h3>
              <p>Se déconnecter de votre compte</p>
              <button onClick={handleLogout} className={styles.logoutButton}>
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;










